"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { CtaButton } from "@/components/layout/CtaButton";
import { submitForm } from "@/lib/submitForm";

const SERVICES = [
  { id: "self-publishing", label: "Self Publishing", value: "self-publishing" },
  { id: "book-editing", label: "Book Editing", value: "book-editing" },
  { id: "book-cover-design", label: "Book Cover Design", value: "book-cover-design" },
  { id: "book-printing", label: "Book Printing", value: "book-printing" },
  { id: "book-marketing", label: "Book Marketing", value: "book-marketing" },
  { id: "pr-and-publicity", label: "PR & Publicity", value: "pr-and-publicity" },
  { id: "book-illustration", label: "Book Illustration", value: "book-illustration" },
  { id: "ghostwriting", label: "Ghostwriting", value: "ghostwriting" },
  { id: "book-formatting", label: "Book Formatting", value: "book-formatting" },
] as const;

const GENRES = [
  { value: "fiction", label: "Fiction" },
  { value: "non-fiction", label: "Non-fiction" },
  { value: "informative", label: "Informative" },
  { value: "biography", label: "Biography" },
  { value: "autobiography", label: "Autobiography" },
  { value: "memoir", label: "Memoir" },
  { value: "childrens-book", label: "Childrens' Book" },
] as const;

export type ProcessLeadFormData = {
  manuscriptReady: string;
  publishedBefore: string;
  bookType: string;
  services: string[];
  name: string;
  email: string;
  phone: string;
  message: string;
};

export type ProcessLeadFormProps = {
  id?: string;
  formHeading?: string;
  submitLabel?: string;
  onSubmit?: (data: ProcessLeadFormData) => void | Promise<void>;
  className?: string;
};

const selectChevron =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23ffffff' d='M1.41 0 6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E\")";

export function ProcessLeadForm({
  id = "service-form",
  formHeading = "Is Your Manuscript The Next Bestseller?",
  submitLabel = "Submit Now",
  onSubmit,
  className,
}: ProcessLeadFormProps) {
  const router = useRouter();
  const [formData, setFormData] = useState<ProcessLeadFormData>({
    manuscriptReady: "",
    publishedBefore: "",
    bookType: "",
    services: [],
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const fieldId = (name: string) => `${id}-${name}`;

  const handleServiceToggle = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(value)
        ? prev.services.filter((s) => s !== value)
        : [...prev.services, value],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");

    try {
      const result = await submitForm({
        source: id,
        fields: {
          ...formData,
          services: formData.services.join(", "),
        },
        // CRM services field wants the human-readable labels the visitor
        // actually saw ("Self Publishing"), not the kebab-case option values
        // ("self-publishing") used internally for the checkboxes.
        services: formData.services.map(
          (value) => SERVICES.find((service) => service.value === value)?.label ?? value,
        ),
        honeypot,
      });

      if (!result.ok) {
        setStatus("error");
        setStatusMessage(result.error);
        return;
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      const ppcData =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem("ubp:ppc")
          : null;

      if (
        typeof window !== "undefined" &&
        Array.isArray(
          (window as unknown as { dataLayer?: unknown[] }).dataLayer
        )
      ) {
        (window as unknown as { dataLayer: unknown[] }).dataLayer.push({
          event: "process_lead_form_submit",
          ...formData,
          ppc: ppcData ? JSON.parse(ppcData) : null,
        });
      }

      setFormData({
        manuscriptReady: "",
        publishedBefore: "",
        bookType: "",
        services: [],
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      router.push("/thank-you/");
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setStatusMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClass =
    "form-control form-select cursor-pointer appearance-none bg-[length:12px] bg-[right_12px_center] bg-no-repeat [&_option]:bg-white [&_option]:text-[#333]";

  return (
    <div
      id={id}
      className={["form-wrap relative z-10", className].filter(Boolean).join(" ")}
    >
      <div className="form-heading">
        <h2 className="font-heading font-50 uppercase text-white">
          {formHeading}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Honeypot — hidden from real users, bots that autofill every field will trip it */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
        />

        <div className="form-group mb-[15px]">
          <label htmlFor={fieldId("manuscriptReady")} className="sr-only">
            Do you have a manuscript ready?
          </label>
          <select
            id={fieldId("manuscriptReady")}
            name="manuscriptReady"
            value={formData.manuscriptReady}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                manuscriptReady: e.target.value,
              }))
            }
            className={selectClass}
            style={{ backgroundImage: selectChevron }}
            required
          >
            <option value="">Do you have a manuscript ready?</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="form-group mb-[15px]">
          <label htmlFor={fieldId("publishedBefore")} className="sr-only">
            Have you published before?
          </label>
          <select
            id={fieldId("publishedBefore")}
            name="publishedBefore"
            value={formData.publishedBefore}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                publishedBefore: e.target.value,
              }))
            }
            className={selectClass}
            style={{ backgroundImage: selectChevron }}
            required
          >
            <option value="">Have you published before?</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div className="form-group mb-[15px]">
          <label htmlFor={fieldId("bookType")} className="sr-only">
            What genre of book do you plan on publishing?
          </label>
          <select
            id={fieldId("bookType")}
            name="bookType"
            value={formData.bookType}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, bookType: e.target.value }))
            }
            className={selectClass}
            style={{ backgroundImage: selectChevron }}
            required
          >
            <option value="">What Genre of book do you plan on publishing?</option>
            {GENRES.map((genre) => (
              <option key={genre.value} value={genre.value}>
                {genre.label}
              </option>
            ))}
          </select>
        </div>

        <div className="service-select text-white">
          <p>What services are you looking for?</p>
          <ul className="mt-[13px] flex list-none flex-wrap gap-y-[15px] p-0">
            {SERVICES.map((service) => {
              const inputId = fieldId(service.id);
              const checked = formData.services.includes(service.value);
              return (
                <li
                  key={service.value}
                  className="flex w-1/2 items-center pl-0 sm:w-1/3"
                >
                  <input
                    type="checkbox"
                    id={inputId}
                    name="services"
                    value={service.value}
                    checked={checked}
                    onChange={() => handleServiceToggle(service.value)}
                    className="service-select-check"
                  />
                  <label htmlFor={inputId}>{service.label}</label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="form-group mt-3 mb-3">
          <label htmlFor={fieldId("name")} className="sr-only">
            Your name
          </label>
          <input
            type="text"
            id={fieldId("name")}
            name="name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Your name here"
            required
            className="form-control"
          />
        </div>

        <div className="form-group mb-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={fieldId("email")} className="sr-only">
              Your email
            </label>
            <input
              type="email"
              id={fieldId("email")}
              name="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="info@example.com"
              required
              className="form-control"
            />
          </div>
          <div>
            <label htmlFor={fieldId("phone")} className="sr-only">
              Your phone
            </label>
            <input
              type="tel"
              id={fieldId("phone")}
              name="phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
              placeholder="(999) 999 9999"
              required
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group mb-3">
          <label htmlFor={fieldId("message")} className="sr-only">
            Your message
          </label>
          <textarea
            id={fieldId("message")}
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, message: e.target.value }))
            }
            placeholder="Your Message"
            rows={4}
            required
            className="form-control resize-y"
          />
        </div>

        <div className="btn-container mt-3">
          <CtaButton
            type="submit"
            variant="primary"
            size="md"
            className="s1-form-submit w-full border-0 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : submitLabel}
          </CtaButton>
        </div>

        {status !== "idle" && (
          <p
            role="status"
            className={
              status === "success"
                ? "mt-3 text-sm text-white"
                : "mt-3 text-sm text-red-300"
            }
          >
            {statusMessage}
          </p>
        )}
      </form>
    </div>
  );
}
